const TARGET_SAMPLE_RATE = 16_000;
const MAX_RECORDING_SECONDS = 300;
const MIN_RECORDING_BYTES = TARGET_SAMPLE_RATE;
const DETECTION_FRAME_SAMPLES = 320;
const MIN_ACTIVE_FRAME_COUNT = 3;
const ACTIVE_FRAME_PEAK = 80;
const ACTIVE_FRAME_RMS = 24;

/**
 * 把多个 Int16 PCM 分块合并成连续缓冲区。
 *
 * @param {Array<Int16Array>} chunks 录音工作线程返回的 PCM 分块。
 * @returns {Int16Array} 合并后的 PCM。
 */
export function mergePcmChunks(chunks) {
  const sampleCount = chunks.reduce((total, chunk) => total + chunk.length, 0);
  const merged = new Int16Array(sampleCount);
  let offset = 0;
  for (const chunk of chunks) {
    merged.set(chunk, offset);
    offset += chunk.length;
  }
  return merged;
}

/**
 * 将 16kHz、16-bit、单声道 PCM 封装成 WAV Blob。
 *
 * @param {Int16Array} pcm 原始 PCM 样本。
 * @param {number} sampleRate 采样率，默认 16000Hz。
 * @returns {Blob} 可上传和播放的 WAV。
 */
export function pcmToWavBlob(pcm, sampleRate = TARGET_SAMPLE_RATE) {
  const buffer = new ArrayBuffer(44 + pcm.byteLength);
  const view = new DataView(buffer);
  const writeText = (offset, text) => {
    for (let index = 0; index < text.length; index += 1) view.setUint8(offset + index, text.charCodeAt(index));
  };
  writeText(0, "RIFF");
  view.setUint32(4, 36 + pcm.byteLength, true);
  writeText(8, "WAVE");
  writeText(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeText(36, "data");
  view.setUint32(40, pcm.byteLength, true);
  new Int16Array(buffer, 44).set(pcm);
  return new Blob([buffer], { type: "audio/wav" });
}

/**
 * 判断录音是否包含连续的有效声音帧。
 *
 * @param {Int16Array} pcm PCM 样本。
 * @returns {boolean} 是否通过最小长度和能量检查。
 */
export function hasAudibleSignal(pcm) {
  if (pcm.byteLength < MIN_RECORDING_BYTES) return false;

  // 按 20ms 帧判断，避免短句前后的静音把整段 RMS 拉低。
  let activeFrameCount = 0;
  for (let offset = 0; offset < pcm.length; offset += DETECTION_FRAME_SAMPLES) {
    const frameEnd = Math.min(offset + DETECTION_FRAME_SAMPLES, pcm.length);
    let framePeak = 0;
    let frameSquaredTotal = 0;
    for (let index = offset; index < frameEnd; index += 1) {
      const sample = pcm[index];
      framePeak = Math.max(framePeak, Math.abs(sample));
      frameSquaredTotal += sample * sample;
    }
    const frameRms = Math.sqrt(frameSquaredTotal / (frameEnd - offset));
    if (framePeak >= ACTIVE_FRAME_PEAK && frameRms >= ACTIVE_FRAME_RMS) {
      activeFrameCount += 1;
      if (activeFrameCount >= MIN_ACTIVE_FRAME_COUNT) return true;
    } else activeFrameCount = 0;
  }
  return false;
}

/**
 * 管理 iPhone 前台录音、PCM 重采样和资源释放。
 */
export class AudioRecorder {
  /**
   * 创建录音器。
   *
   * @param {{onLevel?: Function, onElapsed?: Function, onLimit?: Function}} callbacks 录音状态回调。
   */
  constructor({ onLevel = () => {}, onElapsed = () => {}, onLimit = () => {} } = {}) {
    this.onLevel = onLevel;
    this.onElapsed = onElapsed;
    this.onLimit = onLimit;
    this.context = null;
    this.stream = null;
    this.source = null;
    this.processor = null;
    this.silentGain = null;
    this.chunks = [];
    this.startedAt = 0;
    this.timer = null;
    this.flushResolver = null;
    this.startPromise = null;
    this.startSequence = 0;
  }

  /**
   * 串行请求麦克风，避免快速点击或自动开麦产生多个录音器和计时器。
   *
   * @returns {Promise<void>} 麦克风开始采集后的结果。
   */
  async start() {
    if (this.startPromise) return this.startPromise;
    if (this.isRecording()) return;
    const sequence = ++this.startSequence;
    const startPromise = this.startCapture(sequence);
    this.startPromise = startPromise;
    try {
      await startPromise;
    } finally {
      if (this.startPromise === startPromise) this.startPromise = null;
    }
  }

  /**
   * 完成一次实际的麦克风和 AudioWorklet 初始化。
   *
   * @param {number} sequence 当前启动代次，用于识别后台释放后的过期异步结果。
   * @returns {Promise<void>} 录音采集节点初始化结果。
   */
  async startCapture(sequence) {
    this.chunks = [];
    if (!this.hasReusableStream()) {
      this.cleanup({ releaseStream: true });
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        });
        if (sequence !== this.startSequence) {
          stream.getAudioTracks().forEach((track) => track.stop());
          return;
        }
        this.stream = stream;
      } catch (error) {
        if (sequence !== this.startSequence) return;
        if (error?.name === "NotAllowedError") throw new Error("请允许 Speaking Studio 使用麦克风；首次允许后，前台练习期间不会重复询问");
        throw error;
      }
    }
    try {
      const context = new AudioContext();
      this.context = context;
      if (context.state === "suspended") await context.resume();
      if (sequence !== this.startSequence) return this.cleanup({ releaseStream: true });
      await context.audioWorklet.addModule("/pcm-recorder-worklet.js");
      if (sequence !== this.startSequence) return this.cleanup({ releaseStream: true });
      this.source = this.context.createMediaStreamSource(this.stream);
      this.processor = new AudioWorkletNode(this.context, "pcm-recorder");
      this.silentGain = this.context.createGain();
      this.silentGain.gain.value = 0;
      this.processor.port.onmessage = (event) => this.handleWorkletMessage(event);
      this.source.connect(this.processor);
      this.processor.connect(this.silentGain).connect(this.context.destination);
    } catch (error) {
      this.cleanup();
      if (sequence !== this.startSequence) return;
      throw error;
    }
    if (sequence !== this.startSequence) return this.cleanup({ releaseStream: true });
    this.startedAt = Date.now();
    const timer = window.setInterval(() => {
      if (this.timer !== timer || !this.startedAt) {
        window.clearInterval(timer);
        return;
      }
      const elapsed = (Date.now() - this.startedAt) / 1_000;
      this.onElapsed(elapsed);
      if (elapsed >= MAX_RECORDING_SECONDS) {
        window.clearInterval(timer);
        if (this.timer === timer) this.timer = null;
        this.onLimit();
      }
    }, 200);
    this.timer = timer;
  }

  /**
   * 接收 AudioWorklet 的 PCM、音量和刷新完成事件。
   *
   * @param {MessageEvent} event 工作线程消息。
   * @returns {void}
   */
  handleWorkletMessage(event) {
    if (event.data?.type === "chunk") this.chunks.push(new Int16Array(event.data.pcm));
    if (event.data?.type === "level") this.onLevel(Number(event.data.rms || 0));
    if (event.data?.type === "flushed" && this.flushResolver) {
      this.flushResolver();
      this.flushResolver = null;
    }
  }

  /**
   * 刷新剩余 PCM 并结束录音。
   *
   * @returns {Promise<{pcm: Int16Array, wav: Blob, durationSeconds: number}>} 完整录音。
   */
  async stop() {
    if (!this.isRecording()) throw new Error("当前没有正在进行的录音");
    const durationSeconds = (Date.now() - this.startedAt) / 1_000;
    await new Promise((resolve) => {
      this.flushResolver = resolve;
      this.processor.port.postMessage({ type: "flush" });
      window.setTimeout(resolve, 500);
    });
    const pcm = mergePcmChunks(this.chunks);
    this.cleanup();
    if (pcm.byteLength < MIN_RECORDING_BYTES) throw new Error("录音太短，请至少说 0.5 秒");
    if (!hasAudibleSignal(pcm)) throw new Error("没有检测到足够清晰的声音，请重新录制");
    return { pcm, wav: pcmToWavBlob(pcm), durationSeconds };
  }

  /**
   * 放弃当前录音；默认保留首次授权的麦克风流供下一轮复用。
   *
   * @param {{releaseStream?: boolean}} options 是否同时释放麦克风流。
   * @returns {void}
   */
  cancel({ releaseStream = false } = {}) {
    this.startSequence += 1;
    this.chunks = [];
    this.cleanup({ releaseStream });
  }

  /**
   * 判断录音器当前是否正在采集录音。
   *
   * @returns {boolean} 当前录音状态。
   */
  isRecording() {
    return Boolean(this.processor);
  }

  /**
   * 判断麦克风是否仍处于异步启动阶段。
   *
   * @returns {boolean} 是否正在建立录音采集节点。
   */
  isStarting() {
    return Boolean(this.startPromise);
  }

  /**
   * 判断已授权的麦克风流是否仍可用于下一轮录音。
   *
   * @returns {boolean} 是否存在活动音频轨。
   */
  hasReusableStream() {
    return Boolean(this.stream?.active !== false && this.stream?.getAudioTracks().some((track) => track.readyState === "live" && track.enabled !== false && !track.muted));
  }

  /**
   * 在播放模型语音前释放旧输入轨道，避免 iOS 音频通道切换后复用无采样轨道。
   *
   * @returns {void}
   */
  releaseReusableStream() {
    if (this.isRecording() || !this.stream) return;
    this.stream.getAudioTracks().forEach((track) => track.stop());
    this.stream = null;
  }

  /**
   * 完全释放麦克风流和当前录音资源。
   *
   * @returns {void}
   */
  release() {
    this.cancel({ releaseStream: true });
  }

  /**
   * 断开音频节点并清理计时器，可选完全停止媒体轨。
   *
   * @param {{releaseStream?: boolean}} options 是否完全停止并删除媒体流。
   * @returns {void}
   */
  cleanup({ releaseStream = false } = {}) {
    const timer = this.timer;
    this.timer = null;
    window.clearInterval(timer);
    if (this.processor) this.processor.disconnect();
    if (this.source) this.source.disconnect();
    if (this.silentGain) this.silentGain.disconnect();
    if (this.stream && releaseStream) this.stream.getAudioTracks().forEach((track) => track.stop());
    if (this.context) this.context.close().catch(() => {});
    this.context = null;
    if (releaseStream) this.stream = null;
    this.source = null;
    this.processor = null;
    this.silentGain = null;
    this.startedAt = 0;
    this.onLevel(0);
  }
}

/**
 * 把最长若干秒的多段 PCM 合并为评分音频，控制手机内存和请求体大小。
 *
 * @param {Array<Int16Array>} recordings 多轮回答 PCM。
 * @param {number} secondsPerRecording 每轮最多抽取秒数。
 * @returns {Blob|null} 合并 WAV；没有录音时返回 null。
 */
export function buildAssessmentWav(recordings, secondsPerRecording = 15) {
  const maxSamples = Math.max(1, secondsPerRecording) * TARGET_SAMPLE_RATE;
  const chunks = recordings.filter(Boolean).map((pcm) => pcm.slice(0, maxSamples));
  if (chunks.length === 0) return null;
  return pcmToWavBlob(mergePcmChunks(chunks));
}
